async function fetchPublicFontBase64(path: string): Promise<string> {
  const res = await fetch(path)
  if (!res.ok) throw new Error(`Font yüklenemedi: ${path}`)
  const buf = await res.arrayBuffer()
  let binary = ''
  const bytes = new Uint8Array(buf)
  const chunk = 0x8000
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk))
  }
  return btoa(binary)
}

function stripInlineMarkdown(text: string): string {
  return text
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
}

type PdfLine = { text: string; fontSize: number; bold: boolean; gapBefore: number }

function markdownToPdfLines(markdown: string): PdfLine[] {
  const lines = markdown.replace(/\r\n/g, '\n').split('\n')
  const out: PdfLine[] = []
  let index = 0

  while (index < lines.length) {
    const trimmed = lines[index].trim()

    if (!trimmed || trimmed === '---') {
      index += 1
      continue
    }

    if (trimmed.startsWith('|')) {
      const tableLines: string[] = []
      while (index < lines.length && lines[index].trim().startsWith('|')) {
        tableLines.push(lines[index].trim())
        index += 1
      }
      const rows = tableLines
        .filter((row) => !row.includes('---'))
        .map((row) =>
          row
            .split('|')
            .slice(1, -1)
            .map((cell) => stripInlineMarkdown(cell.trim()))
            .join(' | '),
        )
      for (const row of rows) {
        out.push({ text: row, fontSize: 9, bold: false, gapBefore: 2 })
      }
      continue
    }

    if (trimmed.startsWith('> ')) {
      const quoteLines: string[] = []
      while (index < lines.length && lines[index].trim().startsWith('> ')) {
        quoteLines.push(stripInlineMarkdown(lines[index].trim().slice(2)))
        index += 1
      }
      out.push({
        text: quoteLines.join(' '),
        fontSize: 10,
        bold: false,
        gapBefore: 6,
      })
      continue
    }

    if (trimmed.startsWith('# ')) {
      out.push({ text: stripInlineMarkdown(trimmed.slice(2)), fontSize: 16, bold: true, gapBefore: 10 })
      index += 1
      continue
    }

    if (trimmed.startsWith('## ')) {
      out.push({ text: stripInlineMarkdown(trimmed.slice(3)), fontSize: 13, bold: true, gapBefore: 8 })
      index += 1
      continue
    }

    if (trimmed.startsWith('### ')) {
      out.push({ text: stripInlineMarkdown(trimmed.slice(4)), fontSize: 11, bold: true, gapBefore: 6 })
      index += 1
      continue
    }

    if (trimmed.startsWith('- ')) {
      while (index < lines.length && lines[index].trim().startsWith('- ')) {
        out.push({
          text: `• ${stripInlineMarkdown(lines[index].trim().slice(2))}`,
          fontSize: 10,
          bold: false,
          gapBefore: 2,
        })
        index += 1
      }
      continue
    }

    const paragraphLines: string[] = [stripInlineMarkdown(trimmed)]
    index += 1
    while (index < lines.length) {
      const next = lines[index].trim()
      if (
        !next ||
        next.startsWith('#') ||
        next.startsWith('>') ||
        next.startsWith('|') ||
        next.startsWith('- ') ||
        next === '---'
      ) {
        break
      }
      paragraphLines.push(stripInlineMarkdown(next))
      index += 1
    }

    out.push({
      text: paragraphLines.join(' '),
      fontSize: 10,
      bold: false,
      gapBefore: 4,
    })
  }

  return out
}

export async function exportLegalDocumentPdf(
  title: string,
  version: string,
  markdown: string,
  fileSlug: string,
) {
  const { jsPDF } = await import('jspdf')
  const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' })

  const variableFileName = 'Outfit-Variable.ttf'
  const boldFileName = 'Outfit-Bold.ttf'
  const [variableBase64, boldBase64] = await Promise.all([
    fetchPublicFontBase64(`/fonts/${variableFileName}`),
    fetchPublicFontBase64(`/fonts/${boldFileName}`),
  ])

  doc.addFileToVFS(variableFileName, variableBase64)
  doc.addFileToVFS(boldFileName, boldBase64)
  doc.addFont(variableFileName, 'Outfit', 'normal')
  doc.addFont(boldFileName, 'Outfit', 'bold')

  const pageWidth = doc.internal.pageSize.getWidth()
  const marginX = 48
  const maxWidth = pageWidth - marginX * 2
  let y = 52

  const ensureSpace = (height: number) => {
    const pageHeight = doc.internal.pageSize.getHeight()
    if (y + height > pageHeight - 48) {
      doc.addPage()
      y = 52
    }
  }

  doc.setFont('Outfit', 'bold')
  doc.setFontSize(15)
  doc.text(title, marginX, y)
  y += 18
  doc.setFont('Outfit', 'normal')
  doc.setFontSize(9)
  doc.setTextColor(100, 116, 139)
  doc.text(`Sürüm ${version}`, marginX, y)
  doc.setTextColor(17, 24, 39)
  y += 16

  for (const line of markdownToPdfLines(markdown)) {
    y += line.gapBefore
    doc.setFont('Outfit', line.bold ? 'bold' : 'normal')
    doc.setFontSize(line.fontSize)
    const wrapped = doc.splitTextToSize(line.text, maxWidth) as string[]
    ensureSpace(wrapped.length * (line.fontSize + 3))
    doc.text(wrapped, marginX, y)
    y += wrapped.length * (line.fontSize + 3)
  }

  const safeName = fileSlug.replace(/[^a-z0-9-]+/gi, '-')
  doc.save(`${safeName}-v${version}.pdf`)
}
